'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui';
import { AlertCircle } from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';
import { formatErrorForDisplay } from '@/shared/lib/error-formatter';

const logger = createLogger('ErrorPage');

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('common');
  const [showFullStack, setShowFullStack] = useState(false);
  const errorDisplay = formatErrorForDisplay(error);

  useEffect(() => {
    logger.error('Application error:', error.message, error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <h2
          // eslint-disable-next-line no-restricted-syntax -- non-product error page display heading
          className="mt-4 text-2xl font-bold text-foreground"
        >
          {t('errors.title')}
        </h2>
        <p className="mt-2 text-muted-foreground">{t('errors.generic')}</p>
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 text-left">
            <details className="mb-2">
              <summary
                // eslint-disable-next-line no-restricted-syntax -- dev-only diagnostic disclosure
                className="cursor-pointer text-sm text-muted-foreground"
              >
                Error details (development only)
              </summary>
              <pre
                // eslint-disable-next-line no-restricted-syntax -- dev-only diagnostic stack trace, monospace pre
                className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs whitespace-pre-wrap"
              >
                {showFullStack
                  ? errorDisplay.fullStack
                  : errorDisplay.shortStack}
              </pre>
            </details>
            {errorDisplay.canShowFull && (
              <button
                onClick={() => setShowFullStack(!showFullStack)}
                // eslint-disable-next-line no-restricted-syntax -- dev-only diagnostic show-more toggle
                className="text-xs text-teal-600 hover:text-teal-800"
              >
                {showFullStack ? 'Show less' : 'Show full stack trace'}
              </button>
            )}
          </div>
        )}
        <Button onClick={reset} className="mt-6">
          {t('tryAgain')}
        </Button>
      </div>
    </div>
  );
}
